import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";

import { Icon } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";

import { useNavigateSearch } from "@/premium/hooks";

import "./Breadcrumbs.scss";

const PrettyNamesMap: { [key: string]: { name: string; params: string[], parentOverride?: string } } = {
  "": { name: "home", params: ["client"] },
  "compare-plans": { name: "comparePlans", params: ["client"] },
  plan: { name: "planDetails", params: ["client", "enrollmentPlan"], parentOverride: "/plan-details" },
  plans: { name: "plans", params: ["client"] },
  "plan-details": { name: "plans", params: ["client", "enrollmentPlan"], parentOverride: "/plans" },
  "medicare-prescription-payment-plan": { name: "mppp", params: ["client", "enrollmentPlan"] },
  "find-pharmacies": { name: "findPharmacies", params: ["client", "enrollmentPlan"] },
  pharmacies: { name: "results", params: ["client", "enrollmentPlan", "pharmacyName", "zipCode"] },
  "pharmacy-details": { name: "pharmacyDetails", params: ["client", "enrollmentPlan", "providerId"] },
};

const TRANSLATION_PATH = "BREADCRUMBS";

export interface BreadcrumbsProps {
  readonly planName?: string | undefined;
}

export const Breadcrumbs: FC<BreadcrumbsProps> = (props: BreadcrumbsProps) => {
  const { planName } = props;
  const location = useLocation();
  const navigate = useNavigateSearch();
  const [pathParts, setPathParts] = useState<string[]>([]);
  const [searchParams, _] = useSearchParams();
  const { t: translate } = useTranslation();

  useEffect(() => {
    setPathParts(location.pathname.split("/"));
  }, [location.pathname]);

  const onPathPartClicked = useCallback(
    (pathPart: string) => {
      const parentPath = pathParts.slice(0, pathParts.indexOf(pathPart) + 1).join("/") || "/";
      const destination = PrettyNamesMap[pathPart]?.parentOverride || parentPath;
      const retainedPath = PrettyNamesMap[pathPart].params.reduce((accum, value) => {
        return { ...accum, [value]: searchParams.get(value) };
      }, {});
      navigate(destination, retainedPath);
    },
    [pathParts, navigate, searchParams],
  );

  return (
    <Shelf className="breadcrumbs-wrap-shelf" spacing="small">
      {pathParts.map((pathPart, idx) => {
        return (
          <div className="breadcrumbs-inner" key={pathPart + idx}>
            <Shelf spacing="small">
              {idx !== 0 && idx < pathParts.length && <Icon name="chevron-right" />}
              <p className="breadcrumbs-text" onClick={() => onPathPartClicked(pathPart)}>
                {translate(`${TRANSLATION_PATH}.${PrettyNamesMap[pathPart].name}`)}
              </p>
            </Shelf>
          </div>
        );
      })}
      {planName && (
        <>
          <Icon name="chevron-right" />
          <p className="breadcrumbs-text">{planName.toUpperCase()}</p>
        </>
      )}
      <Shelf.Spacer />
    </Shelf>
  );
};
